import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Trophy,
  ChartLineUp,
  ChartLineDown,
  Clock,
  Lock,
  TrendUp,
  TrendDown,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import IndexStrip from "../components/IndexStrip";
import Seo from "../components/Seo";
import { tradingViewStockUrl } from "../lib/tradingview";
import MonthlyHeatmap from "../components/backtest/MonthlyHeatmap";
import PerformanceDashboard from "../components/PerformanceDashboard";

const periods = [30, 60, 90, 180, 365];

const fmt = (n, d = 2) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d })
    : "—";

function StatCell({ label, value, accent, suffix = "" }) {
  return (
    <div className="border border-subtle bg-surface rounded-sm p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
        {label}
      </div>
      <div className={"font-mono text-2xl tabular-nums " + (accent || "text-primary-fg")}>
        {value}
        {suffix && <span className="text-sm text-muted-fg ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    target_hit: { label: "Target", cls: "bg-pos-soft text-pos border-pos-soft" },
    sl_hit: { label: "SL", cls: "bg-neg-soft text-neg border-neg-soft" },
    exited: { label: "Exit", cls: "bg-mod-soft text-mod border-mod-soft" },
  };
  const c = map[status] || map.exited;
  return (
    <span
      className={
        "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
        c.cls
      }
    >
      {c.label}
    </span>
  );
}

export default function Backtest() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/backtest/summary?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const o = data?.overall;
  const isPremium = data?.is_premium_view;

  return (
    <div className="min-h-screen bg-app grain" data-testid="backtest-page">
      <Seo
        title="Backtest · Breakout Scanner — historical NSE breakout performance"
        description="Transparent track record: win-rate, average P&L, hold days and signal-by-signal history of every NSE breakout closed by Breakout Scanner."
        url="/backtest"
      />
      <IndexStrip />

      <div className="py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/dashboard"
            data-testid="backtest-back"
            className="mb-8 flex items-center gap-2 text-muted-fg hover:text-primary-fg text-xs font-mono uppercase tracking-widest"
          >
            <ArrowLeft size={14} /> Back to dashboard
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <Logo size={28} />
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-muted-fg">
              Breakout Scanner
            </div>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos mb-3">
            /// Historical Backtest
          </div>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-primary-fg mb-4 leading-tight">
            Every closed signal — receipts on the table.
          </h1>
          <p className="text-base text-secondary-fg mb-8 max-w-3xl">
            Transparent track record of NSE breakouts our scanner has detected and closed. Win-rate
            includes all exits — target hits, stop-losses, and EMA-10 rule exits. No cherry-picking.
          </p>

          {/* Top-of-page live track-record dashboard — same component as
              the landing page so the headline numbers (Profit Factor,
              Risk-Reward, Scanner vs NIFTY) stay in lock-step. */}
          <div className="mb-10">
            <PerformanceDashboard />
          </div>

          {/* Period selector */}
          <div className="flex gap-2 mb-8 flex-wrap" data-testid="backtest-periods">
            {periods.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`period-${d}`}
                className={
                  "px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest border transition " +
                  (days === d
                    ? "bg-pos-soft border-pos text-pos"
                    : "border-subtle text-muted-fg hover:text-primary-fg hover:border-pos/40")
                }
              >
                Last {d}d
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center text-muted-fg py-20" data-testid="backtest-loading">
              Loading backtest…
            </div>
          ) : !o || o.count === 0 ? (
            <div className="border border-subtle bg-surface rounded-sm p-10 text-center" data-testid="backtest-empty">
              <Clock size={28} className="mx-auto text-muted-fg mb-3" />
              <div className="text-primary-fg font-medium mb-1">No closed signals in this window</div>
              <div className="text-sm text-muted-fg">Try a longer period.</div>
            </div>
          ) : (
            <>
              {/* Headline stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="backtest-overall">
                <StatCell
                  label="Total closed"
                  value={o.count}
                />
                <StatCell
                  label="Win rate"
                  value={`${o.win_rate}`}
                  suffix="%"
                  accent="text-pos"
                />
                <StatCell
                  label="Avg P&L"
                  value={`${o.avg_pnl_pct >= 0 ? "+" : ""}${o.avg_pnl_pct}`}
                  suffix="%"
                  accent={o.avg_pnl_pct >= 0 ? "text-pos" : "text-neg"}
                />
                <StatCell
                  label="Median hold"
                  value={o.median_hold_days || "—"}
                  suffix={o.median_hold_days ? "days" : ""}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
                <StatCell label="Wins (target)" value={o.wins} accent="text-pos" />
                <StatCell label="Losses (SL)" value={o.losses} accent="text-neg" />
                <StatCell label="Exits (rule)" value={o.exits} />
                <StatCell
                  label="Best · Worst"
                  value={`+${o.best_pct}% · ${o.worst_pct}%`}
                />
              </div>

              {/* Breakdown by type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                <div className="border border-subtle bg-surface rounded-sm p-5" data-testid="backtest-short-term">
                  <div className="flex items-center gap-2 mb-3">
                    <ChartLineUp size={14} weight="bold" className="text-pos" />
                    <div className="font-mono text-xs uppercase tracking-widest text-primary-fg">
                      Short-term breakouts
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-xs font-mono">
                    <div>
                      <div className="text-muted-fg uppercase tracking-widest text-[9px]">Closed</div>
                      <div className="text-primary-fg text-base mt-0.5">{data.short_term.count}</div>
                    </div>
                    <div>
                      <div className="text-muted-fg uppercase tracking-widest text-[9px]">WR</div>
                      <div className="text-pos text-base mt-0.5">{data.short_term.win_rate}%</div>
                    </div>
                    <div>
                      <div className="text-muted-fg uppercase tracking-widest text-[9px]">Avg P&L</div>
                      <div
                        className={
                          "text-base mt-0.5 " +
                          (data.short_term.avg_pnl_pct >= 0 ? "text-pos" : "text-neg")
                        }
                      >
                        {data.short_term.avg_pnl_pct >= 0 ? "+" : ""}
                        {data.short_term.avg_pnl_pct}%
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border border-subtle bg-surface rounded-sm p-5" data-testid="backtest-long-term">
                  <div className="flex items-center gap-2 mb-3">
                    <Trophy size={14} weight="bold" className="text-pos" />
                    <div className="font-mono text-xs uppercase tracking-widest text-primary-fg">
                      Long-term breakouts
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-xs font-mono">
                    <div>
                      <div className="text-muted-fg uppercase tracking-widest text-[9px]">Closed</div>
                      <div className="text-primary-fg text-base mt-0.5">{data.long_term.count}</div>
                    </div>
                    <div>
                      <div className="text-muted-fg uppercase tracking-widest text-[9px]">WR</div>
                      <div className="text-pos text-base mt-0.5">{data.long_term.win_rate}%</div>
                    </div>
                    <div>
                      <div className="text-muted-fg uppercase tracking-widest text-[9px]">Avg P&L</div>
                      <div
                        className={
                          "text-base mt-0.5 " +
                          (data.long_term.avg_pnl_pct >= 0 ? "text-pos" : "text-neg")
                        }
                      >
                        {data.long_term.avg_pnl_pct >= 0 ? "+" : ""}
                        {data.long_term.avg_pnl_pct}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly P&L heatmap */}
              <div className="mb-10" data-testid="backtest-heatmap-section">
                <MonthlyHeatmap months={24} />
              </div>

              {/* Recent table */}
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos">
                  /// Recent closed signals
                </div>
                {!isPremium && (
                  <Link
                    to="/pricing"
                    data-testid="backtest-upgrade-cta"
                    className="text-xs font-mono uppercase tracking-widest text-pos hover:underline flex items-center gap-1"
                  >
                    <Lock size={11} /> Unlock all rows
                  </Link>
                )}
              </div>

              <div className="border border-subtle bg-surface rounded-sm overflow-hidden" data-testid="backtest-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs font-mono">
                    <thead>
                      <tr className="border-b border-subtle bg-surface-hover/40">
                        <th className="text-left p-3 text-muted-fg uppercase tracking-widest text-[9px]">Symbol</th>
                        <th className="text-left p-3 text-muted-fg uppercase tracking-widest text-[9px]">Type</th>
                        <th className="text-right p-3 text-muted-fg uppercase tracking-widest text-[9px]">Entry</th>
                        <th className="text-right p-3 text-muted-fg uppercase tracking-widest text-[9px]">Close</th>
                        <th className="text-right p-3 text-muted-fg uppercase tracking-widest text-[9px]">P&L %</th>
                        <th className="text-center p-3 text-muted-fg uppercase tracking-widest text-[9px]">Hold</th>
                        <th className="text-center p-3 text-muted-fg uppercase tracking-widest text-[9px]">Exit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent.map((s) => {
                        const pos = (s.pnl_pct ?? 0) >= 0;
                        const sym = (s.symbol || "").replace(".NS", "");
                        // Stable per-row key: symbol + signal_type + closed/scanned timestamp
                        // — uniquely identifies each closed signal row.
                        const rowKey = `${s.symbol}-${s.signal_type}-${s.closed_at || s.scanned_at || s.entry}`;
                        return (
                          <tr key={rowKey} className="border-b border-subtle last:border-0 hover:bg-surface-hover/30 transition">
                            <td className="p-3">
                              <a
                                href={tradingViewStockUrl(s.symbol)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary-fg hover:text-pos font-medium"
                                data-testid={`bt-row-sym-${sym}`}
                              >
                                {sym} ↗
                              </a>
                              <div className="text-muted-fg text-[10px] mt-0.5 normal-case font-sans">
                                {s.company_name?.slice(0, 28)}
                              </div>
                            </td>
                            <td className="p-3 text-muted-fg uppercase tracking-widest text-[10px]">
                              {s.signal_type === "short_term" ? "Short" : "Long"}
                            </td>
                            <td className="p-3 text-right text-primary-fg tabular-nums">
                              ₹{fmt(s.entry)}
                            </td>
                            <td className="p-3 text-right text-primary-fg tabular-nums">
                              ₹{fmt(s.close_price)}
                            </td>
                            <td
                              className={
                                "p-3 text-right tabular-nums font-medium flex items-center justify-end gap-1 " +
                                (pos ? "text-pos" : "text-neg")
                              }
                            >
                              {pos ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
                              {pos ? "+" : ""}
                              {fmt(s.pnl_pct)}%
                            </td>
                            <td className="p-3 text-center text-muted-fg tabular-nums">
                              {s.hold_days ? `${s.hold_days}d` : "—"}
                            </td>
                            <td className="p-3 text-center">
                              <StatusBadge status={s.status} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {!isPremium && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-6 border border-pos/40 rounded-sm p-5 bg-pos-soft/40 flex items-center justify-between gap-4 flex-wrap"
                  data-testid="backtest-upgrade-block"
                >
                  <div className="flex items-center gap-3">
                    <Lock size={20} className="text-pos shrink-0" />
                    <div>
                      <div className="font-medium text-primary-fg">Showing 5 of {data.overall.count} rows</div>
                      <div className="text-sm text-secondary-fg">
                        Unlock the full historical track record with the ₹5 trial.
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/pricing"
                    className="px-5 py-2.5 rounded-sm text-sm font-medium"
                    style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
                  >
                    Start ₹5 trial →
                  </Link>
                </motion.div>
              )}

              <div className="mt-8 text-xs text-muted-fg leading-relaxed">
                <ChartLineDown size={12} weight="bold" className="inline mr-1" />
                <b className="text-secondary-fg">Note:</b> Past performance is not indicative of future
                returns. Win-rate and P&L are computed on signals as detected by the scanner; actual
                trading outcomes depend on entry price, slippage, and personal risk management.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
